<script>
  import { extent } from 'd3-array';
  import { format } from 'date-fns';
  import { scaleBand } from 'd3-scale';
  import { scaleSequential } from 'd3-scale';
  import { dateParser } from '../../utils/d3-utils';
  import { interpolateRgb } from 'd3-interpolate';
  import { capfirst, intcomma } from 'journalize';
  import { getSortByString, getSortByNumber } from '../../utils/sorters';

  // the hook for supplied props
  export let data;
  export let include_percapita;

  const getNewCasesHeader = () => {
    const dt = dateParser(data.max_date);
    const diff = new Date().getDate() - dt.getDate();
    if (diff < 6) {
      return format(dt, 'EEEE');
    } else {
      return format(dt, 'MMM d');
    }
  };

  let headers = [];
  // Prep the data
  if (include_percapita) {
    headers = [
      { text: 'Area', class: 'text-left sortable', attr: 'name' },
      {
        text: 'Cases',
        class: 'text-right sortable desktop-only',
        attr: 'confirmed_cases',
      },
      {
        text: 'Per 10k',
        class: 'text-right sortable desktop-only',
        attr: 'confirmed_cases_per_100k',
      },
      {
        text: 'Last 14 days',
        class: 'text-right sortable',
        attr: 'new_confirmed_cases_fourteen_day_total',
      },
      {
        text: 'Per 10k',
        class: 'text-right sortable',
        attr: 'new_confirmed_cases_fourteen_day_per_100k',
      },
    ];
  } else {
    headers = [
      { text: 'Area', class: 'text-left sortable', attr: 'name' },
      {
        text: 'Cases',
        class: 'text-right sortable',
        attr: 'confirmed_cases',
      },
      {
        text: getNewCasesHeader(),
        class: 'text-right sortable',
        attr: 'new_confirmed_cases',
      },
    ];
  }
  let sortAttr = include_percapita
    ? 'new_confirmed_cases_fourteen_day_per_100k'
    : 'confirmed_cases';
  let sortAscending = false;
  let sortFunctions = {
    name: {
      true: getSortByString('sort_text', true),
      false: getSortByString('sort_text', false),
    },
    confirmed_cases: {
      true: getSortByNumber('confirmed_cases', true),
      false: getSortByNumber('confirmed_cases', false),
    },
    confirmed_cases_per_100k: {
      true: getSortByNumber('confirmed_cases_per_100k', true),
      false: getSortByNumber('confirmed_cases_per_100k', false),
    },
    new_confirmed_cases: {
      true: getSortByNumber('new_confirmed_cases', true),
      false: getSortByNumber('new_confirmed_cases', false),
    },
    new_confirmed_cases_fourteen_day_total: {
      true: getSortByNumber('new_confirmed_cases_fourteen_day_total', true),
      false: getSortByNumber('new_confirmed_cases_fourteen_day_total', false),
    },
    new_confirmed_cases_fourteen_day_per_100k: {
      true: getSortByNumber('new_confirmed_cases_fourteen_day_per_100k', true),
      false: getSortByNumber(
        'new_confirmed_cases_fourteen_day_per_100k',
        false
      ),
    },
  };

  // Table row limit
  const limit = 10;
  let showAll = false;

  // computed values
  const getSortText = (t) => {
    if (t.includes(':')) {
      return t.split(':')[1].trim();
    } else {
      return t.trim();
    }
  };
  let preppedData = data.places.map((d) => {
    d.search_text = d.name.toUpperCase();
    d.sort_text = getSortText(d.name);
    return d;
  });
  $: filteredData = preppedData;
  $: tableRows = filteredData.sort(sortFunctions[sortAttr][sortAscending]);
  $: enoughRows = tableRows.length > limit;

  // d3 chart config
  const chartWidth = 90;
  const chartHeight = 15;

  const colorFunctions = data.places.reduce((obj, v) => {
    const values = extent(v.timeseries);
    const scale = scaleSequential(values, interpolateRgb('#E2F2F6', '#00435c'));
    obj[v.name] = (value, previousValue) => {
      if (previousValue === 0 && value > 5) {
        return scale(1);
      } else {
        return scale(value);
      }
    };
    return obj;
  }, {});

  const xDomain = data.places[0].timeseries.map((d, i) => i);
  const xExtent = extent(xDomain);
  const xScale = scaleBand()
    .range([0, chartWidth])
    .domain(xDomain)
    .padding(0.1);
  const xBandwidth = xScale.bandwidth();

  // Legend
  const legendColors = [
    '#E2F2F6',
    '#BEE9F4',
    '#8FD0E1',
    '#60B7D3',
    '#3995B7',
    '#026F90',
    '#00435c',
  ];
  const xLegendScale = scaleBand()
    .range([0, chartWidth])
    .domain(legendColors.map((d, i) => i))
    .padding(0.025);
  const xLegendBandwidth = xLegendScale.bandwidth();

  // flip the toggle on each click
  function onToggle() {
    showAll = !showAll;
  }

  // Filter table
  function filterRows(e) {
    showAll = false;
    const value = e.target.value.toUpperCase();
    if (value == '') {
      filteredData = preppedData;
    } else {
      filteredData = preppedData.filter(
        (d) => d.search_text.indexOf(value) > -1
      );
    }
  }

  function onSort(e) {
    const thisAttr = e.target.getAttribute('data-attr');
    if (thisAttr === sortAttr) {
      sortAscending = !sortAscending;
    } else {
      sortAttr = thisAttr;
      sortAscending = true;
    }
  }
</script>

<div>
  <table class="dg-table">
    <thead>
      <tr>
        {#each headers as obj}
          <th
            scope="col"
            data-attr={obj.attr}
            class={obj.class}
            class:sort-asc={sortAttr === obj.attr && sortAscending}
            class:sort-desc={sortAttr === obj.attr && !sortAscending}
            style={obj.style}
            on:click={onSort}
          >
            {obj.text}
          </th>
        {/each}
        <th scope="col" class="text-center pill-chart">
          New cases
          <div class="pill-chart-legend-wrapper">
            <svg width={chartWidth} height="25px" role="graphics-document">
              <g data-layer="swatches" role="graphics-object">
                {#each legendColors as obj, idx}
                  <g
                    class="legend-swatch"
                    data-index={idx}
                    data-color={obj}
                    role="graphics-symbol"
                  >
                    <rect
                      height="10px"
                      width={xLegendBandwidth}
                      x={xLegendScale(idx)}
                      fill={obj}
                    />
                  </g>
                {/each}
              </g>
              <g
                class="axes x-axis tiny-axis"
                transform="translate(0, 21)"
                width="{chartWidth}px;"
              >
                <text text-anchor="start">Fewer</text>
                <text text-anchor="end" transform="translate({chartWidth}, 0)">
                  More
                </text>
              </g>
            </svg>
          </div>
        </th>
      </tr>
    </thead>
    <tbody>
      {#each tableRows as obj, idx}
        <tr class:display-none={enoughRows && idx + 1 > limit && !showAll}>
          <th scope="row" class="text-left">{capfirst(obj.name)}</th>
          <td class="text-right" class:desktop-only={include_percapita}>
            {obj.confirmed_cases_note || intcomma(obj.confirmed_cases)}
          </td>
          {#if include_percapita}
            <td
              class="text-right lighter desktop-only"
              style="padding-right: 5px;"
            >
              {@html obj.confirmed_cases_per_100k
                ? intcomma((obj.confirmed_cases_per_100k / 10).toFixed(0))
                : '&mdash;'}
            </td>
            <td class="text-right left-border" style="padding-right: 5px;">
              {@html obj.new_confirmed_cases_fourteen_day_total
                ? intcomma(
                    obj.new_confirmed_cases_fourteen_day_total.toFixed(0)
                  )
                : '&mdash;'}
            </td>
            <td class="text-right lighter" style="padding-right: 5px;">
              {@html obj.new_confirmed_cases_fourteen_day_per_100k
                ? intcomma(
                    (
                      obj.new_confirmed_cases_fourteen_day_per_100k / 10
                    ).toFixed(0)
                  )
                : '&mdash;'}
            </td>
          {:else}
            <td class="text-right" style="padding-right: 5px;">
              +{intcomma(obj.new_confirmed_cases)}
            </td>
          {/if}
          <td class="text-center pill-chart left-border">
            <div class="pill-chart-wrapper">
              <svg
                width={chartWidth}
                height={idx === 0 ? chartHeight + 14 : chartHeight}
                transform="translate(0, {idx === 0 ? 8 : 5})"
                role="graphics-document"
              >
                <g class="pill-group" role="graphics-object">
                  {#each obj.timeseries as day, didx}
                    <rect
                      class="day"
                      data-index={didx}
                      data-previous-value={didx <= 7
                        ? null
                        : obj.timeseries[didx - 7]}
                      data-value={day}
                      fill={colorFunctions[obj.name](
                        day,
                        didx <= 7 ? null : obj.timeseries[didx - 7]
                      )}
                      x={xScale(didx)}
                      width={xBandwidth}
                      height="15px"
                      role="graphics-symbol"
                    />
                  {/each}
                </g>
                {#if idx === 0}
                  <g
                    class="axes x-axis tiny-axis"
                    transform="translate(0, {chartHeight + 12})"
                    role="graphics-symbol"
                  >
                    <text text-anchor="start">
                      {format(dateParser(data.min_date), 'MMM d')}
                    </text>
                  </g>
                  <g
                    class="axes x-axis tiny-axis"
                    transform="translate({chartWidth}, {chartHeight + 12})"
                    role="graphics-symbol"
                  >
                    <text text-anchor="end">
                      {format(dateParser(data.max_date), 'MMM d')}
                    </text>
                  </g>
                {/if}
              </svg>
            </div>
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
  <div class="text-center">
    {#if enoughRows}
      <button class="btn" on:click={onToggle}>
        {showAll ? 'Show less' : 'Show all'}
      </button>
    {/if}
  </div>
</div>
